import {
  Box,
  Button,
  TextInput,
  Grid,
  Group,
  Select,
  Switch,
  Textarea,
  Alert,
  Text,
} from '@mantine/core'
import { UseFormReturnType, useForm } from '@mantine/form'
import { yupResolver } from 'mantine-form-yup-resolver'
import { Database } from 'types/supabase'
import * as yup from 'yup'
import { useCreateCreative, useUpdateCreative } from 'hooks/creatives'
import { useQueryAdvertisers } from 'hooks/advertisers'
import { useNavigate } from 'react-router-dom'
import { Map, Marker } from '@vis.gl/react-google-maps'
import { EditPicture, TranslateButton } from 'ui'
import {
  useState,
  useCallback,
  useRef,
  ComponentProps,
} from 'react'
import { useAppTranslation } from 'hooks/useTranslation'
import { isLocationFormatValid } from 'utils'
import { IconAlertCircle } from '@tabler/icons-react'

interface CreativeFormProps {
  isNew?: boolean
  creative?: Database['public']['Tables']['creatives']['Row'] & {
    advertiser: { id: string; name: string }
  }
  onCreated?: (creative: { id: string; title: string | null }) => void
}

// Detail-page meta row while edited in the form (strings; nulls applied on save).
type FormDetailRow = {
  icon: string
  main: string
  sub: string
  main_pt: string
  sub_pt: string
}

// A single EN/PT input pair, each side carrying a TranslateButton that fills it
// from the opposite language (mirrors the Event form's description fields).
type BilingualFieldProps = {
  form: UseFormReturnType<any>
  enField: string
  ptField: string
  label: string
  placeholder?: string
  multiline?: boolean
  minRows?: number
}

const BilingualField = ({
  form,
  enField,
  ptField,
  label,
  placeholder,
  multiline,
  minRows,
}: BilingualFieldProps) => {
  const Comp = multiline ? Textarea : TextInput
  const pair: Array<{ field: string; other: string; lang: 'en' | 'pt' }> = [
    { field: enField, other: ptField, lang: 'en' },
    { field: ptField, other: enField, lang: 'pt' },
  ]
  return (
    <>
      {pair.map(({ field, other, lang }) => (
        <Grid.Col span={6} key={field}>
          <Comp
            label={`${label} (${lang})`}
            placeholder={placeholder}
            minRows={minRows}
            autosize={multiline || undefined}
            rightSectionWidth={44}
            {...form.getInputProps(field)}
            rightSection={
              <TranslateButton
                originalText={(form.values[other] as string) || ''}
                tragetLang={lang}
                onTranslated={(translated) =>
                  form.setFieldValue(field, translated)
                }
              />
            }
          />
        </Grid.Col>
      ))}
    </>
  )
}

export const CreativeForm = (props: CreativeFormProps) => {
  const { isNew = false, creative, onCreated } = props
  const navigate = useNavigate()
  // Two purpose-built images: 1:1 for the ad card, 16:9 for the detail hero.
  const [cardImageFile, setCardImageFile] = useState<File>()
  const [detailImageFile, setDetailImageFile] = useState<File>()
  const [cardImageError, setCardImageError] = useState(false)
  const [detailImageError, setDetailImageError] = useState(false)
  const { t } = useAppTranslation()

  const { advertisers, isLoading: advertisersLoading } = useQueryAdvertisers()

  const createCreative = useCreateCreative((created) => {
    if (onCreated && created && created.id) {
      onCreated({ id: created.id, title: created.title })
    } else {
      navigate(-1)
    }
  })

  const updateCreative = useUpdateCreative(() => {
    navigate(-1)
  })

  const schema = yup.object().shape({
    advertiser_id: yup
      .string()
      .required(t('creatives.advertiser_required')),
    title: yup.string().nullable(),
    description: yup.string().nullable(),
    cta_text: yup.string().nullable(),
    cta_link: yup
      .string()
      .nullable()
      .url(t('creatives.cta_link_invalid')),
  })

  const form = useForm({
    initialValues: {
      advertiser_id: creative?.advertiser_id || '',
      title: creative?.title || '',
      title_pt: creative?.title_pt || '',
      description: creative?.description || '',
      cta_text: creative?.cta_text || '',
      cta_text_pt: creative?.cta_text_pt || '',
      cta_link: creative?.cta_link || '',
      cta_channel: creative?.cta_channel || 'whatsapp',
      // MVP-7 detail-page content
      description_pt: creative?.description_pt || '',
      price_from:
        creative?.price_from !== null && creative?.price_from !== undefined
          ? String(creative.price_from)
          : '',
      price_currency: creative?.price_currency || 'EUR',
      price_caption: creative?.price_caption || '',
      price_caption_pt: creative?.price_caption_pt || '',
      highlights: ((creative?.highlights as unknown as string[] | null) ?? []).join(
        '\n'
      ),
      highlights_pt: (
        (creative?.highlights_pt as unknown as string[] | null) ?? []
      ).join('\n'),
      city: creative?.city || '',
      location_label: creative?.location_label || '',
      // Map-marker coordinates as a single "lat,long" string (mirrors VenueForm).
      location:
        creative?.location_lat != null
          ? `${creative.location_lat},${creative.location_long}`
          : '',
      badge_label: creative?.badge_label || 'Experience',
      badge_label_pt: creative?.badge_label_pt || '',
      advertiser_caption: creative?.advertiser_caption || '',
      advertiser_caption_pt: creative?.advertiser_caption_pt || '',
      is_preview: creative?.is_preview ?? false,
      // Promo/discount code (detail-page coupon block)
      promo_enabled: creative?.promo_enabled ?? false,
      promo_code: creative?.promo_code || '',
      promo_caption: creative?.promo_caption || '',
      promo_caption_pt: creative?.promo_caption_pt || '',
      details: ((creative?.details as unknown as FormDetailRow[] | null) ?? []).map(
        (d) => ({
          icon: d.icon || '',
          main: d.main || '',
          sub: d.sub || '',
          main_pt: d.main_pt || '',
          sub_pt: d.sub_pt || '',
        })
      ),
    },
    validate: yupResolver(schema),
  })

  const iconOptions = [
    { value: 'clock', label: t('creatives.icon_duration') },
    { value: 'currency', label: t('creatives.icon_price') },
    { value: 'mappin', label: t('creatives.icon_location') },
    { value: 'users', label: t('creatives.icon_audience') },
    { value: 'check', label: t('creatives.icon_check') },
  ]

  const formSubmit = async (values: any) => {
    if (isNew) {
      const missingCard = !cardImageFile
      const missingDetail = !detailImageFile
      setCardImageError(missingCard)
      setDetailImageError(missingDetail)
      if (missingCard || missingDetail) {
        return
      }
    }

    const toLines = (val: string) =>
      (val || '')
        .split('\n')
        .map((s: string) => s.trim())
        .filter(Boolean)

    const highlightsArr = toLines(values.highlights)
    const highlightsPtArr = toLines(values.highlights_pt)

    const detailsArr = (values.details || [])
      .filter((d: FormDetailRow) => d.main && d.main.trim())
      .map((d: FormDetailRow) => ({
        icon: d.icon?.trim() || null,
        main: d.main.trim(),
        sub: d.sub?.trim() || null,
        main_pt: d.main_pt?.trim() || null,
        sub_pt: d.sub_pt?.trim() || null,
      }))

    const creativeData = {
      advertiser_id: values.advertiser_id,
      title: values.title || undefined,
      description: values.description || undefined,
      cta_text: values.cta_text || undefined,
      cta_channel: values.cta_channel || 'whatsapp',
      // Only the Website channel uses a custom link; clear it for every other
      // channel so a stale URL from a previous choice never lingers.
      cta_link:
        values.cta_channel === 'website'
          ? values.cta_link?.trim() || null
          : null,
      // MVP-7 detail-page content (null clears the field on update)
      description_pt: values.description_pt?.trim() || null,
      price_from:
        values.price_from === '' || values.price_from == null
          ? null
          : Number(values.price_from),
      price_currency: values.price_currency?.trim() || null,
      price_caption: values.price_caption?.trim() || null,
      highlights: highlightsArr.length ? highlightsArr : null,
      details: detailsArr.length ? detailsArr : null,
      city: values.city?.trim() || null,
      location_label: values.location_label?.trim() || null,
      location_lat: isLocationFormatValid(values.location)
        ? Number(values.location.split(',')[0])
        : null,
      location_long: isLocationFormatValid(values.location)
        ? Number(values.location.split(',')[1])
        : null,
      badge_label: values.badge_label?.trim() || null,
      advertiser_caption: values.advertiser_caption?.trim() || null,
      // Bilingual: PT variants (null clears the field on update)
      title_pt: values.title_pt?.trim() || null,
      cta_text_pt: values.cta_text_pt?.trim() || null,
      price_caption_pt: values.price_caption_pt?.trim() || null,
      highlights_pt: highlightsPtArr.length ? highlightsPtArr : null,
      badge_label_pt: values.badge_label_pt?.trim() || null,
      advertiser_caption_pt: values.advertiser_caption_pt?.trim() || null,
      is_preview: !!values.is_preview,
      // Promo/discount code (only meaningful when a code is set)
      promo_enabled: !!values.promo_enabled,
      promo_code: values.promo_code?.trim() || null,
      promo_caption: values.promo_caption?.trim() || null,
      promo_caption_pt: values.promo_caption_pt?.trim() || null,
    }

    if (isNew) {
      try {
        await createCreative.mutateAsync({
          ...creativeData,
          card_image_file: cardImageFile!,
          detail_image_file: detailImageFile!,
        })
      } catch (error: any) {
        form.setErrors({ general: error.message })
      }
    } else {
      try {
        await updateCreative.mutateAsync({
          id: creative!.id,
          ...creativeData,
          card_image_file: cardImageFile,
          detail_image_file: detailImageFile,
        })
      } catch (error: any) {
        form.setErrors({ general: error.message })
      }
    }
  }

  const advertiserOptions = advertisers.map((advertiser) => ({
    value: advertiser.id,
    label: `${advertiser.name} (${advertiser.category})`,
  }))

  // Stable handler identities for the Google Map. @vis.gl/react-google-maps
  // re-registers its internal event listeners whenever a handler prop's
  // reference changes, so we keep these references stable to avoid that churn.
  //
  // Mantine's useForm returns a BRAND-NEW `form` object on every render, so we
  // cannot depend on `[form]` here — that would recreate the handlers every
  // render and defeat the memoization. Instead we read `form` through a ref and
  // memoize with an empty dep array, giving the map genuinely stable handlers
  // that are attached exactly once.
  const formRef = useRef(form)
  formRef.current = form

  const handleMapClick = useCallback<
    NonNullable<ComponentProps<typeof Map>['onClick']>
  >((e) => {
    if (e.detail.latLng) {
      formRef.current.setFieldValue(
        'location',
        `${e.detail.latLng.lat},${e.detail.latLng.lng}`
      )
    }
  }, [])

  const handleMarkerDragEnd = useCallback<
    NonNullable<ComponentProps<typeof Marker>['onDragEnd']>
  >((e) => {
    if (e.latLng) {
      formRef.current.setFieldValue(
        'location',
        `${e.latLng.lat()},${e.latLng.lng()}`
      )
    }
  }, [])

  return (
    <Box>
      <form onSubmit={form.onSubmit(formSubmit)}>
        <Grid>
          <Grid.Col span={12}>
            <Select
              label={t('creatives.advertiser')}
              placeholder={t('creatives.select_advertiser')}
              data={advertiserOptions}
              disabled={advertisersLoading}
              searchable
              {...form.getInputProps('advertiser_id')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Switch
              label={t('creatives.preview')}
              description={t('creatives.preview_help')}
              {...form.getInputProps('is_preview', { type: 'checkbox' })}
            />
          </Grid.Col>

          <BilingualField
            form={form}
            enField="title"
            ptField="title_pt"
            label={t('creatives.title')}
            placeholder={t('creatives.title_placeholder')}
          />

          <BilingualField
            form={form}
            enField="description"
            ptField="description_pt"
            label={t('creatives.description')}
            placeholder={t('creatives.description_placeholder')}
            multiline
            minRows={3}
          />

          {/* CTA: the sticky "Book here" bar on the mobile Ad Detail screen.
              - channel picks which advertiser contact the button opens (its icon
                + action follow this choice; target comes from the advertiser).
              - CTA Text is the button label (falls back to "Book here" when empty).
              - CTA Link only applies to the Website channel: the mobile app opens
                it if set, otherwise the advertiser's own website. */}
          <Grid.Col span={12}>
            <Select
              label={t('creatives.cta_channel')}
              description={t('creatives.cta_channel_help')}
              data={[
                { value: 'whatsapp', label: t('creatives.cta_channel_whatsapp') },
                { value: 'website', label: t('creatives.cta_channel_website') },
                { value: 'phone', label: t('creatives.cta_channel_phone') },
                { value: 'instagram', label: t('creatives.cta_channel_instagram') },
              ]}
              allowDeselect={false}
              {...form.getInputProps('cta_channel')}
            />
          </Grid.Col>

          <BilingualField
            form={form}
            enField="cta_text"
            ptField="cta_text_pt"
            label={t('creatives.cta_text')}
            placeholder={t('creatives.cta_text_placeholder')}
          />

          {form.values.cta_channel === 'website' && (
            <Grid.Col span={12}>
              <TextInput
                label={t('creatives.cta_link')}
                placeholder={t('creatives.cta_link_placeholder')}
                description={t('creatives.cta_link_help')}
                {...form.getInputProps('cta_link')}
              />
            </Grid.Col>
          )}

          {/* ---- MVP-7 detail-page content (all optional) ---- */}
          <Grid.Col span={12}>
            <Text size="sm" fw={700} mt="md">
              {t('creatives.detail_section')}
            </Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              type="number"
              label={t('creatives.price_from')}
              placeholder={t('creatives.price_from_placeholder')}
              {...form.getInputProps('price_from')}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              label={t('creatives.price_currency')}
              {...form.getInputProps('price_currency')}
            />
          </Grid.Col>

          <BilingualField
            form={form}
            enField="price_caption"
            ptField="price_caption_pt"
            label={t('creatives.price_caption')}
            placeholder={t('creatives.price_caption_placeholder')}
          />

          <BilingualField
            form={form}
            enField="highlights"
            ptField="highlights_pt"
            label={t('creatives.highlights')}
            placeholder={t('creatives.highlights_placeholder')}
            multiline
            minRows={3}
          />

          <Grid.Col span={12}>
            <Text size="sm" fw={500} mb="xs">
              {t('creatives.details')}
            </Text>
            {form.values.details.map((_: FormDetailRow, index: number) => (
              <Box key={index} mb="sm" p="xs" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
                <Group mb="xs" align="flex-end" justify="space-between">
                  <Select
                    label={t('creatives.detail_icon')}
                    data={iconOptions}
                    style={{ flex: 1 }}
                    {...form.getInputProps(`details.${index}.icon`)}
                  />
                  <Button
                    variant="outline"
                    color="red"
                    onClick={() => form.removeListItem('details', index)}
                  >
                    {t('creatives.remove')}
                  </Button>
                </Group>
                <Group grow mb="xs" align="flex-end">
                  <TextInput
                    label={`${t('creatives.detail_main')} (en)`}
                    rightSectionWidth={44}
                    {...form.getInputProps(`details.${index}.main`)}
                    rightSection={
                      <TranslateButton
                        originalText={form.values.details[index].main_pt || ''}
                        tragetLang="en"
                        onTranslated={(tx) =>
                          form.setFieldValue(`details.${index}.main`, tx)
                        }
                      />
                    }
                  />
                  <TextInput
                    label={`${t('creatives.detail_main')} (pt)`}
                    rightSectionWidth={44}
                    {...form.getInputProps(`details.${index}.main_pt`)}
                    rightSection={
                      <TranslateButton
                        originalText={form.values.details[index].main || ''}
                        tragetLang="pt"
                        onTranslated={(tx) =>
                          form.setFieldValue(`details.${index}.main_pt`, tx)
                        }
                      />
                    }
                  />
                </Group>
                <Group grow align="flex-end">
                  <TextInput
                    label={`${t('creatives.detail_sub')} (en)`}
                    rightSectionWidth={44}
                    {...form.getInputProps(`details.${index}.sub`)}
                    rightSection={
                      <TranslateButton
                        originalText={form.values.details[index].sub_pt || ''}
                        tragetLang="en"
                        onTranslated={(tx) =>
                          form.setFieldValue(`details.${index}.sub`, tx)
                        }
                      />
                    }
                  />
                  <TextInput
                    label={`${t('creatives.detail_sub')} (pt)`}
                    rightSectionWidth={44}
                    {...form.getInputProps(`details.${index}.sub_pt`)}
                    rightSection={
                      <TranslateButton
                        originalText={form.values.details[index].sub || ''}
                        tragetLang="pt"
                        onTranslated={(tx) =>
                          form.setFieldValue(`details.${index}.sub_pt`, tx)
                        }
                      />
                    }
                  />
                </Group>
              </Box>
            ))}
            <Button
              variant="light"
              mt="xs"
              onClick={() =>
                form.insertListItem('details', {
                  icon: '',
                  main: '',
                  sub: '',
                  main_pt: '',
                  sub_pt: '',
                })
              }
            >
              {t('creatives.add_detail')}
            </Button>
          </Grid.Col>

          {/* Address is a single field (same in both languages); shown before the
              city, and rendered bold above the city on the mobile detail page. */}
          <Grid.Col span={6}>
            <TextInput
              label={t('creatives.address')}
              placeholder={t('creatives.address_placeholder')}
              {...form.getInputProps('location_label')}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TextInput
              label={t('creatives.city')}
              placeholder={t('creatives.city_placeholder')}
              {...form.getInputProps('city')}
            />
          </Grid.Col>

          {/* Map-marker coordinates: drives the mobile detail-page Google map +
              tap-to-navigate. Optional — leave blank to show only the label. */}
          <Grid.Col span={12}>
            <TextInput
              label={t('creatives.location_coordinates')}
              placeholder="37.5987,-8.8213"
              description={t('creatives.location_coordinates_help')}
              {...form.getInputProps('location')}
            />
            <Box mt="sm" h={300}>
              <Map
                zoom={12}
                center={{
                  lat: isLocationFormatValid(form.values.location)
                    ? Number(form.values.location.split(',')[0])
                    : 37.8022298161398,
                  lng: isLocationFormatValid(form.values.location)
                    ? Number(form.values.location.split(',')[1])
                    : -8.676765624229938,
                }}
                gestureHandling={'greedy'}
                disableDefaultUI={true}
                onClick={handleMapClick}
              >
                {isLocationFormatValid(form.values.location) && (
                  <Marker
                    position={{
                      lat: Number(form.values.location.split(',')[0]),
                      lng: Number(form.values.location.split(',')[1]),
                    }}
                    draggable
                    onDragEnd={handleMarkerDragEnd}
                  />
                )}
              </Map>
            </Box>
          </Grid.Col>

          <BilingualField
            form={form}
            enField="badge_label"
            ptField="badge_label_pt"
            label={t('creatives.badge_label')}
          />

          <BilingualField
            form={form}
            enField="advertiser_caption"
            ptField="advertiser_caption_pt"
            label={t('creatives.advertiser_caption')}
            placeholder={t('creatives.advertiser_caption_placeholder')}
          />

          {/* ---- Promotion code (detail-page coupon block) ---- */}
          <Grid.Col span={12}>
            <Text size="sm" fw={700} mt="md">
              {t('creatives.promo_section')}
            </Text>
          </Grid.Col>

          <Grid.Col span={12}>
            <Switch
              label={t('creatives.promo_enabled')}
              description={t('creatives.promo_enabled_help')}
              {...form.getInputProps('promo_enabled', { type: 'checkbox' })}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <TextInput
              label={t('creatives.promo_code')}
              placeholder={t('creatives.promo_code_placeholder')}
              disabled={!form.values.promo_enabled}
              {...form.getInputProps('promo_code')}
            />
          </Grid.Col>

          <BilingualField
            form={form}
            enField="promo_caption"
            ptField="promo_caption_pt"
            label={t('creatives.promo_caption')}
            placeholder={t('creatives.promo_caption_placeholder')}
          />

          {/* Ad card image — 1:1 square shown in the in-feed AdCard. */}
          <Grid.Col span={6}>
            <Text size="sm" fw={500} mb="xs">
              {t('creatives.card_image')} {isNew && <span style={{ color: 'red' }}>*</span>}
            </Text>
            <EditPicture
              label={t('creatives.card_image_help')}
              defaultValue={creative?.card_image_url}
              width={400}
              height={400}
              onChange={(file) => {
                setCardImageFile(file)
                setCardImageError(false)
              }}
              isError={cardImageError}
            />
            {cardImageError && isNew && (
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                color="red"
                mt="xs"
              >
                {t('creatives.image_required')}
              </Alert>
            )}
          </Grid.Col>

          {/* Ad detail image — 16:9 wide shown in the AdDetail hero. */}
          <Grid.Col span={6}>
            <Text size="sm" fw={500} mb="xs">
              {t('creatives.detail_image')} {isNew && <span style={{ color: 'red' }}>*</span>}
            </Text>
            <EditPicture
              label={t('creatives.detail_image_help')}
              defaultValue={creative?.detail_image_url}
              width={400}
              height={225}
              onChange={(file) => {
                setDetailImageFile(file)
                setDetailImageError(false)
              }}
              isError={detailImageError}
            />
            {detailImageError && isNew && (
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                color="red"
                mt="xs"
              >
                {t('creatives.image_required')}
              </Alert>
            )}
          </Grid.Col>

          {form.errors.general && (
            <Grid.Col span={12}>
              <Alert
                icon={<IconAlertCircle size="1rem" />}
                color="red"
              >
                {form.errors.general}
              </Alert>
            </Grid.Col>
          )}

          <Grid.Col span={12}>
            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={() => navigate(-1)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                loading={createCreative.isPending || updateCreative.isPending}
              >
                {isNew ? t('common.create') : t('common.save')}
              </Button>
            </Group>
          </Grid.Col>
        </Grid>
      </form>
    </Box>
  )
}